import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Loader, Copy, Check, Sparkles,
  Twitter, Linkedin, MessageSquare, Rocket, Mail, Search,
  Send, BarChart3, FileText, RefreshCw
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Strip HTML tags and convert to clean readable text
const cleanHtmlContent = (text) => {
  if (!text) return '';
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(p|div|h[1-6]|li|ul|ol|tr|td|th|table|thead|tbody)[^>]*>/gi, '\n')
    .replace(/<\/?(b|strong|em|i|u|a|span)[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const FormatContent = ({ content }) => {
  const cleaned = cleanHtmlContent(content);
  const blocks = cleaned.split('\n\n').filter(b => b.trim());
  return (
    <div className="pr-12">
      {blocks.map((block, i) => {
        const lines = block.split('\n').filter(l => l.trim());
        const first = lines[0]?.trim() || '';
        const isHeader = /^[A-Z][A-Z\s&/:.-]{3,}$/.test(first) ||
                        /^(EMAIL|SUBJECT|PREVIEW|POST|TWEET|OPTION)/i.test(first) ||
                        /^\d+[\.\)]\s*[A-Z]/.test(first);
        return (
          <div key={`block-${i}`} className="mb-4">
            {isHeader ? (
              <h3 className="text-sm font-bold mb-2 text-[var(--obsidian)] border-b border-[var(--border-subtle)] pb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {first}
              </h3>
            ) : null}
            {lines.slice(isHeader ? 1 : 0).map((line, j) => (
              <p key={j} className="text-sm text-[var(--text-secondary)] leading-relaxed mb-1">{line.trim()}</p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const CONTENT_TYPES = [
  { id: 'twitter', label: 'Twitter / X', icon: Twitter, color: '#1DA1F2' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'reddit', label: 'Reddit', icon: MessageSquare, color: '#FF4500' },
  { id: 'producthunt', label: 'Product Hunt', icon: Rocket, color: '#DA552F' },
  { id: 'email_campaign', label: 'Email Campaigns', icon: Mail, color: '#FF3B30' },
  { id: 'seo', label: 'SEO Package', icon: Search, color: '#34A853' }
];

const CAMPAIGN_TYPES = [
  { id: 'welcome_series', label: 'Welcome Series' },
  { id: 'upgrade_nudge', label: 'Upgrade Nudge' },
  { id: 're_engagement', label: 'Re-engagement' },
  { id: 'announcement', label: 'Feature Announcement' },
  { id: 'signup_outreach', label: 'Sign Up Outreach' }
];

const MarketingHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');
  const [contentType, setContentType] = useState('twitter');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [contentHistory, setContentHistory] = useState([]);
  const [campaignType, setCampaignType] = useState('welcome_series');
  const [generatingCampaign, setGeneratingCampaign] = useState(false);
  const [campaignContent, setCampaignContent] = useState('');
  const [sendingNudge, setSendingNudge] = useState(false);
  const [customContext, setCustomContext] = useState('');
  const [sendEmails, setSendEmails] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState('');
  const [csvEmails, setCsvEmails] = useState('');
  const [segments, setSegments] = useState(null);
  const [campaignHistory, setCampaignHistory] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const [statsRes, historyRes, segRes, campRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/marketing/email-stats`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/marketing/content-history`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/marketing/user-segments`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/marketing/campaigns`).catch(() => ({ data: [] })),
      ]);
      setEmailStats(statsRes.data);
      setContentHistory(historyRes.data || []);
      setSegments(segRes.data);
      setCampaignHistory(campRes.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedContent('');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/marketing/generate-content`,
        { content_type: contentType, additional_context: customContext || undefined },
        { withCredentials: true }
      );
      setGeneratedContent(data.content);
      toast.success('Content generated!');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCampaign = async () => {
    setGeneratingCampaign(true);
    setCampaignContent('');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/marketing/generate-email-campaign`,
        { campaign_type: campaignType, additional_context: customContext || undefined },
        { withCredentials: true }
      );
      setCampaignContent(data.content);
      toast.success('Campaign generated!');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate campaign');
    } finally {
      setGeneratingCampaign(false);
    }
  };

  const handleSendNudge = async (target) => {
    if (!window.confirm(`Send upgrade nudge emails to all ${target.replace('_', ' ')}?`)) return;
    setSendingNudge(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/marketing/send-nudge-emails`,
        { target },
        { withCredentials: true }
      );
      toast.success(data.message);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send nudge');
    } finally {
      setSendingNudge(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!sendSubject.trim() || !campaignContent) {
      toast.error('Generate campaign content and enter a subject line first');
      return;
    }
    const manualRecipients = sendEmails.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    const hasRecipients = manualRecipients.length > 0 || selectedSegment || csvEmails.trim();
    if (!hasRecipients) {
      toast.error('Select a segment, enter email addresses, or paste a list');
      return;
    }

    let confirmMsg = 'Send this campaign to ';
    if (selectedSegment) confirmMsg += `all ${selectedSegment.replace('_', ' ')} (${segments?.[selectedSegment] || '?'} users)`;
    else if (csvEmails.trim()) confirmMsg += `${csvEmails.split(/[\n,]/).filter(e => e.trim().includes('@')).length} emails from list`;
    else confirmMsg += `${manualRecipients.length} recipient(s)`;
    confirmMsg += '?';
    if (!window.confirm(confirmMsg)) return;

    setSendingCampaign(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/marketing/send-campaign`,
        {
          recipients: manualRecipients,
          subject: sendSubject,
          html_content: campaignContent,
          segment: selectedSegment || null,
          csv_emails: csvEmails.trim() || null
        }
      );
      toast.success(`Campaign sent! ${data.sent} delivered, ${data.failed} failed out of ${data.total}`);
      setSendEmails('');
      setCsvEmails('');
      setSelectedSegment('');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleRetryQueued = async () => {
    if (!window.confirm('Retry sending all queued emails?')) return;
    setRetrying(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/marketing/retry-queued`);
      toast.success(`Retried: ${data.sent} sent, ${data.failed} failed out of ${data.total}`);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry');
    } finally {
      setRetrying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { id: 'content', label: 'Content Generator', icon: Sparkles },
    { id: 'campaigns', label: 'Email Campaigns', icon: Mail },
    { id: 'nudge', label: 'Send Nudges', icon: Send },
    { id: 'analytics', label: 'Email Analytics', icon: BarChart3 },
    { id: 'history', label: 'History', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8" data-testid="marketing-hub">
      <Toaster position="top-right" />
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Marketing Hub
        </h1>
        <p className="text-[var(--text-secondary)] mb-8">AI-powered marketing automation for VAULT</p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b-2 border-[var(--border-default)] pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-bold border-2 transition-all ${
                  activeTab === tab.id
                    ? 'bg-[var(--obsidian)] text-white border-[var(--border-default)]'
                    : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)]'
                }`}
              >
                <Icon size={14} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content Generator Tab */}
        {activeTab === 'content' && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {CONTENT_TYPES.map((ct) => {
                const Icon = ct.icon;
                return (
                  <button
                    key={ct.id}
                    onClick={() => setContentType(ct.id)}
                    data-testid={`content-type-${ct.id}`}
                    className={`p-4 border-2 text-center transition-all ${
                      contentType === ct.id
                        ? 'border-[var(--border-default)] bg-[var(--surface)]'
                        : 'border-[var(--border-subtle)] hover:border-[var(--border-default)]'
                    }`}
                  >
                    <Icon size={24} className="mx-auto mb-2" style={{ color: ct.color }} />
                    <div className="text-xs font-bold">{ct.label}</div>
                  </button>
                );
              })}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Additional Context (optional)</label>
              <textarea
                value={customContext}
                onChange={(e) => setCustomContext(e.target.value)}
                placeholder="E.g. We just launched dark mode, targeting solopreneurs..."
                className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none h-20"
                data-testid="custom-context-input"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-content-btn"
              className="bg-[var(--obsidian)] text-white font-bold px-8 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform disabled:opacity-50 flex items-center gap-2 mb-6"
            >
              {generating ? <><Loader size={16} className="animate-spin" /> Generating...</> : <><Sparkles size={16} /> Generate Content</>}
            </button>

            {generatedContent && (
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative">
                <button
                  onClick={() => copyToClipboard(cleanHtmlContent(generatedContent))}
                  className="absolute top-4 right-4 p-2 border-2 border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors"
                  data-testid="copy-content-btn"
                >
                  {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
                <FormatContent content={generatedContent} />
              </div>
            )}
          </div>
        )}

        {/* Email Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div>
            <div className="flex flex-wrap gap-3 mb-6">
              {CAMPAIGN_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  onClick={() => setCampaignType(ct.id)}
                  data-testid={`campaign-type-${ct.id}`}
                  className={`px-4 py-2 text-sm font-bold border-2 transition-all ${
                    campaignType === ct.id
                      ? 'bg-[var(--obsidian)] text-white border-[var(--border-default)]'
                      : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)]'
                  }`}
                >
                  {ct.label}
                </button>
              ))}
            </div>

            <button
              onClick={handleGenerateCampaign}
              disabled={generatingCampaign}
              data-testid="generate-campaign-btn"
              className="bg-[var(--secondary)] text-white font-bold px-8 py-3 border-2 border-[var(--border-default)] hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 mb-6"
            >
              {generatingCampaign ? <><Loader size={16} className="animate-spin" /> Generating...</> : <><Mail size={16} /> Generate Campaign</>}
            </button>

            {campaignContent && (
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative">
                <button
                  onClick={() => copyToClipboard(cleanHtmlContent(campaignContent))}
                  className="absolute top-4 right-4 p-2 border-2 border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors"
                >
                  {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
                <FormatContent content={campaignContent} />

                {/* Send Campaign Form */}
                <div className="mt-6 pt-6 border-t-2 border-[var(--border-default)] space-y-4">
                  <h4 className="text-sm font-black uppercase tracking-wider" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Send This Campaign</h4>
                  
                  {campaignType === 'signup_outreach' && (
                    <div className="p-3 border-2 border-[var(--warning)] bg-[var(--warning)]/10 text-sm">
                      <strong>Sign Up Outreach</strong> — This campaign targets people who are NOT yet VAULT users. Use Option 3 below to paste your prospect email list.
                    </div>
                  )}

                  <input
                    data-testid="campaign-subject-input"
                    type="text"
                    placeholder="Email subject line"
                    value={sendSubject}
                    onChange={(e) => setSendSubject(e.target.value)}
                    className="w-full px-3 py-2 text-sm border-2 border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                  />

                  {/* Option 1: Send to User Segment — hidden for signup outreach */}
                  {campaignType !== 'signup_outreach' && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">Option 1: Send to User Segment</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'all_users', label: 'All Users' },
                        { id: 'free_users', label: 'Free Users' },
                        { id: 'pro_users', label: 'Pro Users' },
                        { id: 'business_users', label: 'Business Users' },
                        { id: 'inactive_users', label: 'Inactive (7d+)' },
                      ].map(seg => (
                        <button
                          key={seg.id}
                          data-testid={`segment-${seg.id}`}
                          onClick={() => setSelectedSegment(selectedSegment === seg.id ? '' : seg.id)}
                          className={`px-3 py-1.5 text-xs font-bold border-2 transition-all ${
                            selectedSegment === seg.id
                              ? 'bg-[var(--secondary)] text-white border-[var(--secondary)]'
                              : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)]'
                          }`}
                        >
                          {seg.label} {segments?.[seg.id] != null ? `(${segments[seg.id]})` : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Option 2: Manual emails — hidden for signup outreach */}
                  {campaignType !== 'signup_outreach' && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">Option 2: Enter Emails</p>
                    <input
                      data-testid="campaign-recipients-input"
                      type="text"
                      placeholder="Comma separated: user@email.com, user2@email.com"
                      value={sendEmails}
                      onChange={(e) => setSendEmails(e.target.value)}
                      className="w-full px-3 py-2 text-sm border-2 border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                    />
                  </div>
                  )}

                  {/* Option 3: Bulk paste / CSV */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">
                      {campaignType === 'signup_outreach' ? 'Paste Prospect Email List' : 'Option 3: Paste Email List'}
                    </p>
                    <textarea
                      data-testid="campaign-csv-input"
                      placeholder="Paste emails here, one per line or comma separated..."
                      value={csvEmails}
                      onChange={(e) => setCsvEmails(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 text-sm border-2 border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)] font-mono resize-y"
                    />
                    {csvEmails.trim() && (
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        {csvEmails.split(/[\n,]/).filter(e => e.trim().includes('@')).length} valid emails detected
                      </p>
                    )}
                  </div>

                  <button
                    data-testid="send-campaign-btn"
                    onClick={handleSendCampaign}
                    disabled={sendingCampaign}
                    className="bg-[var(--primary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center gap-2"
                  >
                    {sendingCampaign ? <><Loader size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Campaign</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Send Nudges Tab */}
        {activeTab === 'nudge' && (
          <div className="space-y-4">
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
              <h3 className="font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Upgrade Nudge - Free Users
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Send upgrade reminder emails to all users on the free plan.
              </p>
              <button
                onClick={() => handleSendNudge('free_users')}
                disabled={sendingNudge}
                data-testid="nudge-free-users-btn"
                className="bg-[var(--primary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center gap-2"
              >
                {sendingNudge ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                Send to Free Users
              </button>
            </div>

            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
              <h3 className="font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Re-engagement - Inactive Users
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Send a nudge to users who haven't been active recently.
              </p>
              <button
                onClick={() => handleSendNudge('inactive_users')}
                disabled={sendingNudge}
                data-testid="nudge-inactive-users-btn"
                className="bg-[var(--warning)] text-[var(--obsidian)] font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center gap-2"
              >
                {sendingNudge ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                Send to Inactive Users
              </button>
            </div>
          </div>
        )}

        {/* Email Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Email Stats</h2>
              <div className="flex gap-2">
                {emailStats?.queued > 0 && (
                  <button
                    data-testid="retry-queued-btn"
                    onClick={handleRetryQueued}
                    disabled={retrying}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-[var(--secondary)] text-white border-2 border-[var(--border-default)] disabled:opacity-50"
                  >
                    {retrying ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Retry Queued ({emailStats.queued})
                  </button>
                )}
                <button onClick={fetchStats} className="p-2 border-2 border-[var(--border-default)] hover:bg-[var(--surface-alt)]">
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>
            {emailStats ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total Emails', value: emailStats.total, color: '#0A0A0A' },
                  { label: 'Sent', value: emailStats.sent, color: '#34A853' },
                  { label: 'Queued', value: emailStats.queued, color: '#007AFF' },
                  { label: 'Failed', value: emailStats.failed, color: '#FF3B30' }
                ].map((stat, i) => (
                  <div key={`stat-${i}`} className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 text-center">
                    <div className="text-3xl font-black" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="text-xs text-[var(--text-secondary)] font-medium mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--text-secondary)]">No email data yet.</p>
            )}

            {/* Campaign History */}
            {campaignHistory.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-black mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Campaign History</h3>
                <div className="space-y-3">
                  {campaignHistory.map((c) => (
                    <div key={c._id} className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-bold">{c.subject || 'Untitled Campaign'}</div>
                        <div className="text-xs text-[var(--text-muted)]">
                          {c.segment ? c.segment.replace('_', ' ') : 'Custom list'} &middot; {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs font-mono">
                        <span className="text-green-600">{c.sent} sent</span>
                        {c.failed > 0 && <span className="text-red-500">{c.failed} failed</span>}
                        <span className="text-[var(--text-muted)]">/ {c.total_recipients} total</span>
                        <span className={`px-2 py-0.5 border text-[10px] font-bold uppercase ${
                          c.status === 'completed' ? 'border-green-500 text-green-600' : 'border-[var(--warning)] text-[var(--warning)]'
                        }`}>
                          {c.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div>
            <h2 className="text-xl font-black mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Generated Content History
            </h2>
            {contentHistory.length > 0 ? (
              <div className="space-y-4">
                {contentHistory.map((item, i) => (
                  <div key={item._id || `content-${i}`} className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                        {item.content_type?.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-[var(--text-muted)]">
                        {item.created_at ? new Date(item.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-6">
                      {item.content?.split('\n').filter(l => l.trim()).slice(0, 6).map((line, j) => (
                        <p key={j} className="mb-1">{line.replace(/^#+\s*/, '')}</p>
                      ))}
                    </div>
                    <button
                      onClick={() => copyToClipboard(item.content)}
                      className="mt-3 text-sm font-medium text-[var(--secondary)] hover:underline flex items-center gap-1"
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--text-secondary)]">No content generated yet. Use the Content Generator tab to get started.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingHub;
